import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import PageHero from "@/components/landing/PageHero";
import SuiteCrossLink from "@/components/landing/SuiteCrossLink";
import { useSEO } from "@/hooks/useSEO";
import { supabase } from "@/integrations/supabase/client";
import {
  ShieldCheck,
  FileCheck,
  ClipboardList,
  Users,
  AlertTriangle,
  FlaskConical,
  GitBranch,
  Building2,
  ScrollText,
  Truck,
  Radar,
  BarChart3,
  Eye,
  Wrench,
  Package,
  Database,
  Bot,
  ArrowRight,
  CheckCircle2,
  Lock,
  Loader2,
  type LucideIcon,
} from "lucide-react";

// The validation-ready workspace lives in its own project (Supabase exryxqxstcklbhbcilrh).
// Signed-out visitors just land on its sign-in page.
const EQMS_APP_URL = "https://eqms.pharmalify.com";

/**
 * Single sign-in path into the validation-ready workspace: /sso/launch drives the
 * eqms-identity handoff (identity provisioning + short-lived signed token).
 * No other mechanism exists any more.
 */
function useEqmsHandoff() {
  const [loading, setLoading] = useState(false);
  const open = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        window.open(EQMS_APP_URL, "_blank", "noopener,noreferrer");
        return;
      }
      window.location.assign(
        `/sso/launch?target=eqms&return_url=${encodeURIComponent(
          `${EQMS_APP_URL}/sso/handoff`,
        )}&next=/`,
      );
    } finally {
      setLoading(false);
    }
  };
  return { open, loading };
}

const modules: { icon: LucideIcon; name: string; urs: string }[] = [
  { icon: FileCheck, name: "Document control", urs: "Review cycles, effective dates, periodic review, redline comparison, PDF renditions, SOP reading library." },
  { icon: ClipboardList, name: "Training management", urs: "Job codes, curricula, auto-assignment, training matrix, quiz bank, competency framework." },
  { icon: AlertTriangle, name: "Deviations", urs: "Structured lifecycle, 5-Whys and Fishbone root cause, mandatory batch impact, triage board." },
  { icon: ShieldCheck, name: "CAPA", urs: "Action classification, effectiveness-check scheduling and ageing, closure gating." },
  { icon: GitBranch, name: "Change management", urs: "Impact and regulatory-impact assessment, phase checklists, verification, approvals." },
  { icon: Users, name: "Complaints & pharmacovigilance", urs: "Intake, MedDRA coding support, reportability decision, response letters, ICH E2B(R3) export." },
  { icon: ScrollText, name: "Audits & Inspector Mode", urs: "Audit programme, GMP/ISO checklists, findings to CAPA, exportable signed evidence pack." },
  { icon: Truck, name: "Suppliers & quality agreements", urs: "Qualification lifecycle, approved-supplier list, scorecards, agreements with RACI." },
  { icon: Radar, name: "Risk management (ICH Q9)", urs: "FMEA, site-specific risk matrices and RPN thresholds, heatmaps, periodic review." },
  { icon: CheckCircle2, name: "Validation", urs: "Validation asset lifecycle, IQ/OQ/PQ execution ledger, automatic deviation on failure." },
  { icon: FlaskConical, name: "Laboratory (LIMS)", urs: "Samples, specifications, results and review, OOS handling, retains, reagents, stability, EM, CoA." },
  { icon: Wrench, name: "Maintenance & calibration (CMMS)", urs: "Assets, PM plans, work orders with ISO 14224 coding, calibration traceability, spare parts." },
  { icon: Package, name: "Materials & batch release", urs: "Material master, batch hold register, pre-release batch checker, CoA / CoC issuance." },
  { icon: BarChart3, name: "APQR / PQR & analytics", urs: "Dataset assembly, Cpk/Ppk trending, findings, narrative drafting, control charts, report catalogue." },
  { icon: Building2, name: "Regulatory intelligence & RIM", urs: "Source ingestion, impact triage, submissions, correspondence and commitments register." },
  { icon: Eye, name: "Compliance workspace", urs: "Requirements traceability matrix, configuration snapshots and diffs, DR drill evidence." },
];

const compliance = [
  { label: "Electronic signatures", detail: "Part 11-style e-signatures with meaning, signer identity, timestamp and step-up re-authentication." },
  { label: "Append-only audit trail", detail: "Per record: who, what, when, previous and new value. Bulk actions still write one entry per record." },
  { label: "Enforced in the data layer", detail: "Role-based access, site scoping and segregation of duties enforced by the database, not only the interface." },
  { label: "Validation-ready", detail: "Validation package with user requirements specifications (URS), a traceability matrix, IQ/OQ/PQ support and configuration evidence." },
  { label: "EU GMP Annex 11 & GAMP 5", detail: "Computerised systems lifecycle with configuration control and validation evidence." },
  { label: "ICH Q9 / Q10 · ALCOA+", detail: "Quality risk management, pharmaceutical quality system and data-integrity principles by design." },
];

const connections = [
  {
    icon: Database,
    title: "Linked records across three domains",
    body: "An out-of-specification result, the investigation it triggers, the instrument that produced it, the calibration that was due, the SOP that governs it and the batch on hold are the same connected data — not four systems reconciled by hand.",
    href: undefined,
  },
  {
    icon: Bot,
    title: "AI Agents — native inside Spine",
    body: "Governed, audit-trailed GxP assistants operate inside Spine’s compliance boundary for document drafting, deviation triage, CAPA assistance and audit readiness. They propose and cite, never decide; authorized people review and approve.",
    href: "/ai-agents",
  },
  {
    icon: Radar,
    title: "Regulatory Watch — native inside Spine",
    body: "Regulatory alerts feed Change Control, Quality Risk Management (QRM) and the SOP lifecycle. Teams assess cited sources, approve actions and trace changes to affected procedures, training and assets within Spine.",
    href: "/regulatory-hub",
  },
];

const Eqms = () => {
  useSEO({
    title: "Pharmalify Spine | Unified GxP Quality, LIMS & CMMS",
    description:
      "Pharmalify Spine unifies Quality (eQMS), QC Lab (LIMS) and Asset Integrity (CMMS) for biopharma, with native AI Agents, Regulatory Watch and validation support.",
    keywords:
      "Pharmalify Spine, biopharma GxP operations, validation-ready QMS, eQMS with LIMS, eQMS with CMMS, 21 CFR Part 11, EU GMP Annex 11, electronic quality management system, CAPA software, deviation management, calibration management, GxP cloud QMS",
    canonical: "https://www.pharmalify.com/spine",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
         name: "Pharmalify Spine",
      description:
        "Unified GxP operations backbone for biopharma: Quality (eQMS), QC Lab (LIMS), Asset Integrity (CMMS), native AI Agents and Regulatory Watch with linked records, a full audit trail and single sign-on.",
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "Electronic Quality Management System (eQMS)",
      operatingSystem: "Web Browser",
      url: "https://www.pharmalify.com/spine",
      offers: { "@type": "Offer", priceCurrency: "USD", category: "enterprise license" },
    },
  });

  const handoff = useEqmsHandoff();

  return (
    <main className="min-h-screen bg-background">
      <PageHero
         title="Pharmalify Spine"
        description="Your unified GxP operations backbone. Pharmalify Spine connects Quality (eQMS), QC Lab (LIMS) and Asset Integrity (CMMS) with linked records, a full audit trail and single sign-on, plus governed AI assistance and Regulatory Watch built in. Modules in scope depend on your licensed configuration."
      />

      {/* Top CTA strip */}
      <section className="bg-muted/30 border-b border-border/60">
        <div className="container py-6 flex flex-col sm:flex-row items-center justify-between gap-4 max-w-6xl">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-accent shrink-0" />
            <p className="text-sm text-muted-foreground">
              Access to Spine is contract-based only, with organization-specific provisioning and agreed scope.
              Request a private walkthrough or sign in to your existing customer workspace.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
            <Button onClick={handoff.open} disabled={handoff.loading} size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground gap-2">
              Customer sign-in
              {handoff.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/contact">Request Walkthrough</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="container max-w-6xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Three pillars. One operational backbone.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Quality / eQMS", body: "Control documents, training, deviations, CAPA, Change Control, audits and quality risk management with connected evidence and accountable approvals." },
              { title: "QC Lab / LIMS", body: "Connect samples, specifications, results, review and OOS investigations to the quality events, procedures and instruments behind each result." },
              { title: "Asset Integrity / CMMS", body: "Manage equipment, calibration, preventive maintenance and work orders alongside laboratory and quality records to assess operational impact." },
            ].map((pillar) => (
              <Card key={pillar.title} className="border-border/60"><CardContent className="p-6">
                <h3 className="font-semibold text-foreground mb-3">{pillar.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{pillar.body}</p>
              </CardContent></Card>
            ))}
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="py-16 md:py-20">
        <div className="container max-w-6xl">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-accent mb-3 px-3 py-1 rounded-full bg-accent/10">
              Connected GxP operations
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
              Quality, QC Lab and Asset Integrity — connected from the start
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Every module is engineered against URS-traceable requirements, with an append-only audit trail,
              e-signature and validation evidence built in.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {modules.map((m) => (
              <Card key={m.name} className="border-border/60 hover:shadow-md hover:border-accent/40 transition-all">
                <CardContent className="p-5 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                    <m.icon className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm leading-tight">{m.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{m.urs}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Connected suite story */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container max-w-6xl">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-accent mb-3 px-3 py-1 rounded-full bg-accent/10">
              Why it is different
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
              The value is in the links between the records
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {connections.map((c) => (
              <div key={c.title} className="relative rounded-2xl border border-border/60 bg-card p-6">
                <c.icon className="w-7 h-7 text-accent mb-3" />
                <h3 className="font-semibold text-foreground mb-2">{c.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">{c.body}</p>
                {c.href && (
                  <Link to={c.href} className="text-sm font-semibold text-accent hover:underline inline-flex items-center gap-1">
                    Learn more <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-16 md:py-20">
        <div className="container max-w-4xl">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-accent mb-3 px-3 py-1 rounded-full bg-accent/10">
              Compliance Foundation
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">Validation-ready by design</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {compliance.map((c) => (
              <div key={c.label} className="flex items-start gap-3 p-5 rounded-xl border border-border/60 bg-card">
                <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-foreground">{c.label}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{c.detail}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-muted-foreground leading-relaxed p-5 rounded-xl border border-border/60 bg-muted/40">
            Formal validation is performed per customer intended use. Spine is validation-ready; the validation package supports your quality team’s risk-based assessment, execution and approval.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container max-w-4xl">
          <div className="relative overflow-hidden rounded-2xl bg-primary border border-accent/20 px-8 py-12 md:px-14 md:py-14 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-3">Contract-based access, scoped to your biopharma</h2>
            <p className="text-primary-foreground/70 max-w-2xl mx-auto mb-7">
              From emerging clinical biotechs to mid-sized biopharmas with hundreds of users across departments and sites, Spine is provisioned under contract — not through a self-serve trial. Agree intended use, deployment scope and validation responsibilities with our team.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground gap-2">
                <Link to="/contact">Request Walkthrough <ArrowRight className="w-4 h-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-primary-foreground/5 hover:bg-primary-foreground/10 text-primary-foreground border-primary-foreground/25 whitespace-normal h-auto py-3">
                <Link to="/contact">Request Validation &amp; Contract Details</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <SuiteCrossLink current="eqms" />
    </main>
  );
};

export default Eqms;
